import { useCallback, useEffect, useRef, useState } from 'react';
import {
    checkForAppUpdate,
    dismissUpdateForBuild,
    reloadApp,
    shouldAutoReloadInAppMode,
} from '../utils/appUpdateCheck';
import { isAppMode } from '../utils/appMode';

const CHECK_INTERVAL_MS = 10 * 60 * 1000;
const APP_MODE_CHECK_INTERVAL_MS = 5 * 60 * 1000;
const AUTO_RELOAD_DELAY_MS = 2500;

/**
 * Polls build-version.json and prompts (or auto-reloads in official app WebView) when a new deploy is live.
 */
export function useAppUpdateCheck() {
    const [updateAvailable, setUpdateAvailable] = useState(false);
    const [remoteBuildId, setRemoteBuildId] = useState<string | null>(null);
    const checkingRef = useRef(false);
    const autoReloadTimerRef = useRef<number | null>(null);

    const clearAutoReloadTimer = useCallback(() => {
        if (autoReloadTimerRef.current != null) {
            window.clearTimeout(autoReloadTimerRef.current);
            autoReloadTimerRef.current = null;
        }
    }, []);

    const runCheck = useCallback(
        async (options?: { fromVisibility?: boolean }) => {
            if (checkingRef.current) return;
            checkingRef.current = true;
            try {
                const result = await checkForAppUpdate();
                if (!result.updateAvailable || !result.remoteBuildId) {
                    setUpdateAvailable(false);
                    setRemoteBuildId(null);
                    return;
                }

                setRemoteBuildId(result.remoteBuildId);

                if (
                    options?.fromVisibility &&
                    shouldAutoReloadInAppMode(result.remoteBuildId)
                ) {
                    console.log('[TRWidget] App update detected in app mode — reloading');
                    reloadApp();
                    return;
                }

                if (shouldAutoReloadInAppMode(result.remoteBuildId)) {
                    setUpdateAvailable(true);
                    clearAutoReloadTimer();
                    autoReloadTimerRef.current = window.setTimeout(() => {
                        console.log('[TRWidget] App update auto-reload (app mode)');
                        reloadApp();
                    }, AUTO_RELOAD_DELAY_MS);
                    return;
                }

                setUpdateAvailable(true);
            } finally {
                checkingRef.current = false;
            }
        },
        [clearAutoReloadTimer]
    );

    const dismissUpdate = useCallback(() => {
        if (remoteBuildId) {
            dismissUpdateForBuild(remoteBuildId);
        }
        clearAutoReloadTimer();
        setUpdateAvailable(false);
    }, [remoteBuildId, clearAutoReloadTimer]);

    const applyUpdate = useCallback(() => {
        clearAutoReloadTimer();
        reloadApp();
    }, [clearAutoReloadTimer]);

    useEffect(() => {
        void runCheck();

        const intervalMs = isAppMode() ? APP_MODE_CHECK_INTERVAL_MS : CHECK_INTERVAL_MS;
        const intervalId = window.setInterval(() => {
            void runCheck();
        }, intervalMs);

        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                void runCheck({ fromVisibility: true });
            }
        };
        document.addEventListener('visibilitychange', onVisibilityChange);

        const onPageShow = (event: PageTransitionEvent) => {
            if (event.persisted) {
                void runCheck({ fromVisibility: true });
            }
        };
        window.addEventListener('pageshow', onPageShow);

        return () => {
            window.clearInterval(intervalId);
            document.removeEventListener('visibilitychange', onVisibilityChange);
            window.removeEventListener('pageshow', onPageShow);
            clearAutoReloadTimer();
        };
    }, [runCheck, clearAutoReloadTimer]);

    return {
        updateAvailable,
        dismissUpdate,
        applyUpdate,
        isAppMode: isAppMode(),
    };
}
